import { useEffect, useState } from 'react';
import { settingsService, CompanySettings } from '../services/settingsService';

/**
 * Shared, module-cached access to the tenant's own company profile
 * (`GET /settings/company`). Statutory documents — GST returns, TDS
 * statements, tax invoices — must be stamped with the FILER'S real legal
 * name, GSTIN and registered address, never a "Your Company Name"
 * placeholder. Fetched once per session and reused across every report page
 * so switching tabs/reports doesn't refetch.
 */
let cached: CompanySettings | null = null;
let inflight: Promise<CompanySettings | null> | null = null;

const load = (): Promise<CompanySettings | null> => {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = settingsService
      .getCompanySettings()
      .then((res) => {
        cached = res?.settings ?? null;
        return cached;
      })
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/** A one-line registered address from whatever address parts are set. */
export const formatCompanyAddress = (company: CompanySettings | null): string => {
  if (!company) return '';
  return [company.address, company.city, company.state, company.pincode]
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .join(', ');
};

export const useCompanySettings = (): CompanySettings | null => {
  const [company, setCompany] = useState<CompanySettings | null>(cached);
  useEffect(() => {
    let active = true;
    load().then((c) => {
      if (active) setCompany(c);
    });
    return () => {
      active = false;
    };
  }, []);
  return company;
};
